import React, { useState } from 'react'
import { Link, navigate } from 'gatsby'
import { useLocation } from '@reach/router'
import styled, { css } from 'styled-components'
import { useStore } from '../hooks/useStore'
import { useAuthHydrationReady } from '../hooks/useAuthHydrationReady'
import { MobileMenuWithHeader } from './MobileMenuWithHeader'
import { MobileUserMenu } from './MobileUserMenu'
import { clearCiamSessionAuth } from '../hooks/useCiamAuthorize'
import { useCiamLogout } from '../hooks/useCiamLogout'
import {
  Box,
  Icon,
  P,
  Ul,
  Span,
  ButtonAnchor,
  Button,
  ScreenReaderText,
  Dropdown,
  DownshiftProvider,
  Avatar,
  IdleTimeout,
  useSessionTimer,
  LoadingSpinner,
  AlertGroup,
} from '@allied-solutions/affinity'
import {
  User,
  UserSolid,
  LogOut01,
} from '@allied-solutions/affinity-react-icons/dist/standard'

import { theme } from '../theme.js'
import { useWindowWidth, useCiamAuthorize } from '../hooks'
import { Logo } from './Logo'
import { LinkAnchor } from './LinkAnchor.js'

const NavBox = styled(Box)``
const StyledSpan = styled(Span)`
  line-height: 26px;
  text-align: right;
  color: rgb(0, 23, 62);
  font-size: 16px;
  font-style: normal;
`

const StyledNavNoAnimation = styled.nav`
  ${props => css`
    padding-top: ${props.theme.space[6]};
    padding-bottom: ${props.theme.space[6]};
    background-color: ${props.theme.colors.white};
    background: white;
    border-bottom: 1px solid ${props.theme.colors.mutedLine};
    position: fixed;
    z-index: 9000;
    height: [80px, 100px];
    width: inherit;

    ${NavBox} {
      padding-top: ${props.theme.space[6]};
      padding-bottom: ${props.theme.space[6]};
      background-color: ${props.theme.colors.white};
      border-bottom: 1px solid ${props.theme.colors.mutedLine};
      position: fixed;
      animation-duration: 0.5s;
      animation-timing-function: ease;
      animation-name: showStickyHeader;
      box-shadow: ${props.theme.shadows.lg};

      ${props => props.theme.mediaQueries.md} {
      }

      ${props.theme.mediaQueries.sm} {
        padding-bottom: ${props.theme.space[6]};
        padding-top: ${props.theme.space[6]};
      }
    }
  `}
`

const mediumBreakpoint = Number(theme[1].replace('px', ''))

const FullWidthAlertWrapper = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 10000;
`

const StyledAlert = styled(AlertGroup.Alert)`
  padding: 30px;

  > div:first-of-type span:nth-of-type(2) {
    width: 100%;
    text-align: left;
  }
`

const GlobalHeaderLoginDropdown = styled(Box)`
  position: relative;
  &:has([aria-expanded='false']) [id='Global__Header__Button--Login-menu'] {
    pointer-events: none;
  }
`

// Mount only when authenticated — useSessionTimer starts counting on hook call,
// independent of whether <IdleTimeout /> is rendered.
const SessionIdleTimeout = ({ onLogout }) => {
  const { handleAuthorize, isAuthorizing } = useCiamAuthorize()
  const sessionTimer = useSessionTimer({
    expireSessionFn: () => Promise.resolve(onLogout('idle')),
    extendSessionFn: () => Promise.resolve(),
    sessionDuration: 1200,
    warningThreshold: 300,
  })

  return (
    <IdleTimeout
      {...sessionTimer}
      sessionStatus={sessionTimer.sessionStatus}
      logoutButtonProps={{
        onClick: () => {
          onLogout('logout')
        },
      }}
      loginButtonProps={{
        onClick: handleAuthorize,
        disabled: isAuthorizing,
      }}
    />
  )
}

const Nav = ({ underlineHome, underlineSupport, liColor }) => {
  const { pathname } = useLocation()
  const windowWidth = useWindowWidth()
  const { dispatch, state } = useStore()
  const authHydrationReady = useAuthHydrationReady()
  const { handleAuthorize, isAuthorizing } = useCiamAuthorize()
  const { handleLogout: handleCiamLogout } = useCiamLogout()
  const [needMenu, setNeedMenu] = useState(false)

  const handleLogout = async logoutType => {
    if (typeof window === 'undefined') {
      return
    }

    const signOffSuccessful = await handleCiamLogout()
    if (!signOffSuccessful) {
      if (logoutType !== 'idle') {
        dispatch({
          type: 'addToState',
          payload: { hasSignoutError: true },
        })
        return
      }
    }

    setTimeout(() => navigate('/'), 0)
    dispatch({
      type: 'addToState',
      payload: {
        userData: null,
        ciamUserId: null,
        hasSessionToken: false,
        ...(logoutType === 'idle'
          ? { TriggeredTimeout: true }
          : { TriggeredLogout: true }),
      },
    })
    clearCiamSessionAuth()
  }

  const mobileMenuProps = {
    needMenu,
    setNeedMenu,
  }
  const MobileMenuComponent = state.hasSessionToken
    ? MobileUserMenu
    : MobileMenuWithHeader

  return (
    <>
      {state.hasSignoutError && (
        <FullWidthAlertWrapper>
          <AlertGroup id="signout-error-alert-group" width="100%">
            <StyledAlert
              id="signout-error-message"
              type="error"
              isDismissable
              py={['6xl', 2]}
              onHidden={() => {
                dispatch({
                  type: 'addToState',
                  payload: { hasSignoutError: false },
                })
              }}
            >
              There was a problem while signing out. Please try again.
            </StyledAlert>
          </AlertGroup>
        </FullWidthAlertWrapper>
      )}
      <StyledNavNoAnimation aria-label="Navigation">
        <NavBox
          id="homeSupportNav"
          display="flex"
          px={[6, 6, 6, 15]}
          py={[7, 7, 7, 8]}
          position="fixed"
          top="0"
          right="0"
          left="0"
          zIndex="dialog"
          css={`
            @media print {
              display: block;
            }
          `}
        >
          <Logo
            id="Global__Header__Logo__Nav"
            css={`
              @media print {
                img {
                  display: block;
                  margin: 0 auto;
                }
              }
            `}
          />
          <Box ml="auto">
            {windowWidth <= mediumBreakpoint ? (
              <MobileMenuComponent {...mobileMenuProps} />
            ) : (
              <Ul
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                css={`
                  > li {
                    margin-left: ${props => props.theme.space[10]};
                  }
                `}
              >
                <li>
                  <LinkAnchor
                    id="Global__Header__Link--home"
                    to={state.hasSessionToken ? '/dashboard/' : '/'}
                  >
                    <StyledSpan
                      className="navSupportLi"
                      typeStyle="labelLarge.medium"
                      textAlign="right"
                      color={liColor}
                    >
                      {state.hasSessionToken ? 'Dashboard' : 'Home'}
                    </StyledSpan>
                  </LinkAnchor>
                  {authHydrationReady &&
                    state.hasSessionToken &&
                    (pathname === '/dashboard' ||
                      pathname === '/dashboard/') && (
                      <Box gradient="blue" width="20" height="1" />
                    )}
                  {authHydrationReady &&
                    !state.hasSessionToken &&
                    underlineHome && (
                      <Box gradient="blue" width="11" height="1" />
                    )}
                </li>
                <li>
                  <LinkAnchor id="Global__Header__Link--support" to="/support/">
                    <StyledSpan
                      className="navSupportLi"
                      typeStyle="labelLarge.medium"
                      textAlign="right"
                      color={liColor}
                    >
                      Support
                    </StyledSpan>
                  </LinkAnchor>
                  {underlineSupport && (
                    <Box gradient="blue" width="17" height="1" />
                  )}
                </li>
                <li>
                  {!state.hasSessionToken && (
                    <ButtonAnchor
                      id="Global__Header__Button--enterReferenceId"
                      forwardedAs={Link}
                      boxShadow="none"
                      to="/enter-reference-id/"
                    >
                      <Span fontSize="16px">Start Verification</Span>
                    </ButtonAnchor>
                  )}
                </li>
                <li style={{ marginLeft: theme.space.xl }}>
                  {state.hasSessionToken ? (
                    <DownshiftProvider.Select
                      id="Global__Header__Button--Login"
                      items={['My Profile', 'Logout']}
                      onSelectedItemChange={({ selectedItem }) => {
                        if (selectedItem === 'My Profile') {
                          navigate('/profile/')
                        } else if (selectedItem === 'Logout') {
                          handleLogout('logout')
                        }
                      }}
                    >
                      <ScreenReaderText
                        as="span"
                        id="Global__Header__Button--Login-label"
                      >
                        User account menu
                      </ScreenReaderText>
                      <GlobalHeaderLoginDropdown>
                        <Dropdown>
                          <Dropdown.Trigger
                            render={props => (
                              <Avatar
                                gradient="teal"
                                size="avatars.lg"
                                {...props}
                              >
                                {state.userData?.Email?.charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                          />
                          <Dropdown.Menu
                            header={
                              <Box
                                display="flex"
                                alignItems="center"
                                gap="sm"
                                py={4}
                                width="100%"
                              >
                                <Avatar gradient="teal" size="avatars.lg">
                                  {state?.userData?.Email?.charAt(
                                    0
                                  ).toUpperCase()}
                                </Avatar>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={0}
                                >
                                  <Box display="flex" alignItems="center">
                                    <P typeStyle="labelSmall.bold" mb="0">
                                      {state?.userData?.Name?.['Given'] ?? ''}
                                    </P>
                                    <P typeStyle="labelSmall.bold" mb="0">
                                      {state?.userData?.Name?.['Family']
                                        ? ` ${state?.userData?.Name?.['Family']}`
                                        : ''}
                                    </P>
                                  </Box>
                                  {state?.userData?.Email}
                                </Box>
                              </Box>
                            }
                            minWidth="200px"
                            top="120%"
                            bottom="auto"
                            left="auto"
                            right="0"
                          >
                            <Dropdown.MenuItem item="My Profile" index={0}>
                              <Box display="flex" alignItems="center" gap="sm">
                                <Icon src={UserSolid} />
                                <P mb="0">My Profile</P>
                              </Box>
                            </Dropdown.MenuItem>
                            <Dropdown.MenuItem item="Logout" index={1}>
                              <Box display="flex" alignItems="center" gap="sm">
                                <Icon src={LogOut01} />
                                <P mb="0">Logout</P>
                              </Box>
                            </Dropdown.MenuItem>
                          </Dropdown.Menu>
                        </Dropdown>
                      </GlobalHeaderLoginDropdown>
                    </DownshiftProvider.Select>
                  ) : (
                    <Button
                      outlined
                      icon={isAuthorizing ? undefined : User}
                      id="Global__Header__Button--profile"
                      disabled={isAuthorizing}
                      onClick={handleAuthorize}
                    >
                      {isAuthorizing ? (
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                        >
                          <LoadingSpinner size="sm" aria-hidden />
                          <ScreenReaderText>Logging in…</ScreenReaderText>
                        </Box>
                      ) : (
                        <ScreenReaderText>Log In</ScreenReaderText>
                      )}
                    </Button>
                  )}
                </li>
              </Ul>
            )}
          </Box>
        </NavBox>
      </StyledNavNoAnimation>
      {state.hasSessionToken && <SessionIdleTimeout onLogout={handleLogout} />}
    </>
  )
}

export { Nav }
